/**
 * appStorePurchase - Native App Store (StoreKit 2 / Electron MAS) purchase
 * bridge for EmberPOS (ticket 4556).
 *
 * Dispatches to whichever native purchase bridge is present:
 *  - Capacitor (iOS): AppStorePurchasePlugin.swift, registered as "AppStorePurchase"
 *  - Electron (macOS MAS): window.electronAPI.appStorePurchase (electron/app-store-purchase.cjs)
 *
 * Ticket 4556 replaces the earlier "no backend, trust the on-device StoreKit
 * transaction directly" design (localStorage-cached entitlement, ~2026-08-13)
 * with real server verification: every purchase/restore round-trips through
 * server/routes/applePurchases.js, which re-checks the transaction against
 * the fleet's shared Apple oracle and binds it to the signed-in tenant. The
 * server response — not the raw StoreKit transaction — is the entitlement of
 * record (tenants.subscription_tier, same column Stripe writes).
 */
import { registerPlugin } from '@capacitor/core';
import { isCapacitor, isElectron, isNativeApp, API_BASE_URL } from '../config';
import { getDeviceToken } from '../lib/authToken';

// Must match server/routes/applePurchases.js PRODUCT_TIERS and the oracle
// registry (qode-ide server/apple-verify.cjs APP_ROWS['ember-pos']).
export const APP_STORE_PRODUCTS = {
  starter: 'com.quantrolix.firepos.starter.monthly',
  professional: 'com.quantrolix.firepos.professional.monthly',
  enterprise: 'com.quantrolix.firepos.enterprise.monthly',
};

const APP_ID = 'fire-pos'; // matches SubscriptionGate's appId prop for this app
const EARLIER_PURCHASE_CLEARED_MESSAGE = 'An earlier App Store purchase for this plan was still waiting and has now been cleared. Please try again.';
const IOSStoreKit = registerPlugin('AppStorePurchase');

// Must match server/config/appleOracle.js's MAX_TRANSACTIONS and
// server/routes/applePurchases.js's /restore rejection — ticket 4556
// finding: the server used to silently .slice(0, 20) an oversized restore
// batch, dropping any transaction past position 20 without a trace; it now
// 400s instead, so a tenant with a long StoreKit history must be chunked
// into batches this size here, with results aggregated across chunks.
const RESTORE_BATCH_SIZE = 20;

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function requireNativeStore() {
  if (!isNativeApp) throw new Error('App Store purchases are only available in the EmberPOS app.');
}

/**
 * True only where a real purchase path exists. `isNativeApp` alone is not
 * enough: it's also true for the non-MAS Electron build (package.json's
 * `dmg`/`zip` mac targets, where `process.mas` is false and `inAppPurchase`
 * throws) and the plain iOS WKWebView wrapper (UA "QuantrolixNative", no
 * Capacitor plugin, no electronAPI bridge).
 */
export function canPurchase() {
  if (isCapacitor) return true;
  if (isElectron) return !!window.electronAPI?.appStorePurchase?.isMas;
  return false;
}

async function apiFetch(path, options = {}) {
  const token = getDeviceToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    const error = new Error(body.error || 'App Store request failed');
    // A refusal of an Apple-verified transaction (another account's, expired,
    // linked elsewhere) still names the ids StoreKit must finish; callers
    // finish exactly those, never everything they sent.
    error.finishTransactionIds = body.finishTransactionIds;
    error.status = res.status;
    throw error;
  }
  return body;
}

async function getAccountToken() {
  const body = await apiFetch('/api/apple-purchases/account-token');
  if (!body.data?.appAccountToken) throw new Error('Unable to prepare App Store purchase');
  return body.data.appAccountToken;
}

function verifyTransaction(transaction) {
  return apiFetch('/api/apple-purchases/verify', {
    method: 'POST',
    body: JSON.stringify({ transactionId: transaction.transactionId, productId: transaction.productId }),
  }); // { data: { tier, status, periodEnd, productId }, finishTransactionIds }
}

export async function getAppStoreProducts() {
  requireNativeStore();
  const productIds = Object.values(APP_STORE_PRODUCTS);
  if (isCapacitor) return (await IOSStoreKit.getProducts({ productIds })).products;
  if (isElectron && window.electronAPI?.appStorePurchase) {
    return (await window.electronAPI.appStorePurchase.getProducts(productIds)).products;
  }
  throw new Error('App Store is unavailable on this device');
}

async function runNativePurchase(productId, appAccountToken) {
  if (isCapacitor) return IOSStoreKit.purchase({ productId, appAccountToken });
  if (isElectron && window.electronAPI?.appStorePurchase) {
    return window.electronAPI.appStorePurchase.purchase(productId, appAccountToken);
  }
  throw new Error('App Store is unavailable on this device');
}

// Mirrors server/routes/applePurchases.js's pickHighestTier tie-break
// (tier rank, then latest expiry) so aggregating one entitlement per chunk
// picks the same "best" answer the server would have picked from a single
// unbounded batch.
const TIER_RANK = { enterprise: 3, professional: 2, starter: 1 };
function betterEntitlement(a, b) {
  if (!a) return b;
  if (!b) return a;
  const rankDiff = (TIER_RANK[b.tier] || 0) - (TIER_RANK[a.tier] || 0);
  if (rankDiff !== 0) return rankDiff > 0 ? b : a;
  return new Date(b.periodEnd || 0).getTime() > new Date(a.periodEnd || 0).getTime() ? b : a;
}

async function finishTransaction(transactionId) {
  if (!transactionId) return;
  if (isCapacitor) return IOSStoreKit.finish({ transactionId });
  if (isElectron && window.electronAPI?.appStorePurchase) {
    return window.electronAPI.appStorePurchase.finish(transactionId);
  }
}

// Finish EXACTLY the transactions the server marked finishable (Apple-verified,
// one of our products) that are ALSO among the ones we sent in that request.
// Never "everything we sent": an unverified one (failed lookup, 503, 404) must
// stay queued so it is retried. Call it only after the server answered, on a
// success AND on a refusal. Unfinished, StoreKit re-delivers a transaction
// forever and refuses a new payment for its product. Resolves to how many
// finished; one StoreKit failure never stops the others.
async function finishMarked(sent, finishTransactionIds) {
  const marked = new Set(Array.isArray(finishTransactionIds) ? finishTransactionIds : []);
  const results = await Promise.allSettled(
    sent.filter((t) => t?.transactionId && marked.has(t.transactionId)).map((t) => finishTransaction(t.transactionId)),
  );
  return results.filter((r) => r.status === 'fulfilled').length;
}

// restoreAppStorePurchases() and the background pending claim post and finish
// the same queued transactions, so they run one at a time: never two requests
// racing over one transaction (double finish, interleaved entitlement merges).
// The chain swallows failures (.catch) so one failed run can never wedge the
// next; the caller of a failed run still sees its own rejection.
let reconcileQueue = Promise.resolve();
function serialized(task) {
  const run = reconcileQueue.then(task);
  reconcileQueue = run.catch(() => {});
  return run;
}

// Shared by restoreAppStorePurchases() and watchPendingTransactions()'s
// claimPending() — ticket 4556 finding: the pending-transactions path used
// to send its whole (unbounded) batch in one request, same bug as restore
// pre-fix. Both send one chunk of raw StoreKit transactions and get back
// the server's { entitlement, finishTransactionIds } for that chunk; the
// caller's own loop decides how to aggregate across chunks and how to
// handle a chunk failing (restore throws, the background claim logs and
// keeps going — see claimPending below).
async function postRestoreBatch(batch) {
  return (await apiFetch('/api/apple-purchases/restore', {
    method: 'POST',
    body: JSON.stringify({ transactions: batch.map((t) => ({ transactionId: t.transactionId })) }),
  })).data;
}

/**
 * Buy a plan and bind it to the signed-in tenant. Requires the caller to
 * already be authenticated (SubscriptionGate.login/register ran first) —
 * appAccountToken comes from the server, keyed to the tenant's JWT.
 * @returns {Promise<{tier, status, periodEnd, productId}>} the server's
 *   entitlement of record, NOT the raw StoreKit transaction.
 */
export async function purchaseAppStorePlan(productId) {
  requireNativeStore();
  const appAccountToken = await getAccountToken();
  const transaction = await runNativePurchase(productId, appAccountToken);
  let body;
  try {
    body = await verifyTransaction(transaction);
  } catch (error) {
    // StoreKit hands back an earlier unfinished transaction for this product
    // instead of a new payment; the server verified it but it grants this
    // account nothing. Finish it (when the server says so) so the next Buy
    // opens a real purchase sheet, and say so instead of repeating the refusal.
    const finished = await finishMarked([transaction], error.finishTransactionIds);
    // Not for a 409 (linked to another EmberPOS account): finishing it is right,
    // but a retry hits the same refusal, so "try again" would be false. Show
    // the server's reason instead.
    if (finished > 0 && error.status !== 409) throw new Error(EARLIER_PURCHASE_CLEARED_MESSAGE);
    throw error;
  }
  // A 200 means the server verified AND granted this exact transaction, so it is
  // always finishable: finish it whether or not the body lists it. Gating on
  // the field would leave every purchase unfinished against a server that does
  // not send it yet (iOS has no pending claim to drain it later). allSettled
  // inside finishMarked: a StoreKit failure never rejects a granted purchase.
  await finishMarked([transaction], [transaction?.transactionId]);
  return body.data;
}

/**
 * Replay StoreKit's restore queue through server verification. Only
 * currently-active transactions grant; every transaction the server marks
 * finishable (Apple-verified and one of our products, active or not, ours or
 * another account's) gets finish()ed so StoreKit stops re-delivering it, and
 * nothing else does. The server rejects (400) any single request over
 * RESTORE_BATCH_SIZE transactions (ticket 4556: it used to silently drop
 * the overflow instead), so a long StoreKit history is sent in chunks and
 * every chunk's result is aggregated — otherwise a tenant with more than
 * RESTORE_BATCH_SIZE transactions could never restore at all.
 * @returns {Promise<{tier, status, periodEnd, productId}>}
 */
export async function restoreAppStorePurchases() {
  requireNativeStore();
  return serialized(restoreOnce);
}

async function restoreOnce() {
  const result = isCapacitor
    ? await IOSStoreKit.restore()
    : isElectron && window.electronAPI?.appStorePurchase
      ? await window.electronAPI.appStorePurchase.restore()
      : await Promise.reject(new Error('App Store is unavailable on this device'));

  const transactions = result?.transactions || [];
  if (transactions.length === 0) throw new Error('No App Store purchases were found for this Apple ID.');

  let bestEntitlement = null;
  // Sequential, not parallel: each chunk grants through the same tenant row
  // (grantToTenant's CAS-guarded UPDATE serializes them), and restore is a rare
  // action — not worth risking a lost-update race for a speedup nobody needs.
  for (const batch of chunk(transactions, RESTORE_BATCH_SIZE)) {
    let data;
    try {
      data = await postRestoreBatch(batch);
    } catch (error) {
      // A refusal (409: linked to another account) still lists the verified
      // transactions to finish; then surface it.
      await finishMarked(batch, error.finishTransactionIds);
      throw error;
    }
    await finishMarked(batch, data.finishTransactionIds);
    bestEntitlement = betterEntitlement(bestEntitlement, data.entitlement);
  }

  if (!bestEntitlement) throw new Error('No active EmberPOS subscription was found for this Apple ID.');
  return bestEntitlement;
}

const CACHED_TIER_KEY = `${APP_ID}_iap_tier_cache`;

/**
 * Cache the tenant's last-known paid tier so code outside the React tree
 * (zustand stores) can gate synchronously without a network round trip.
 * SubscriptionGate.jsx calls this whenever /me, purchase, or restore
 * resolves. This is a same-session UI convenience only — the server (and
 * the tenants.subscription_tier column) stays the actual source of truth;
 * nothing server-side ever reads this cache back into a grant decision.
 */
export function setCachedTier(tier) {
  try {
    if (tier && tier !== 'free') localStorage.setItem(CACHED_TIER_KEY, tier);
    else localStorage.removeItem(CACHED_TIER_KEY);
  } catch { /* localStorage unavailable */ }
}

export function getCachedTier() {
  try { return localStorage.getItem(CACHED_TIER_KEY); } catch { return null; }
}

export function isPremiumTier(tier) {
  return !!tier && tier !== 'free';
}

const CACHED_LOCATION_LIMIT_KEY = `${APP_ID}_location_limit_cache`;

/**
 * Cache the tenant's real, per-tier location_limit (not just "is this
 * tenant premium at all") so useLocationStore's client-side cap can bind
 * correctly for Starter (1) and Professional (3), not only "free vs paid" —
 * ticket 4556 finding: isPremiumTier() alone treats every paid tier as
 * unlimited. -1 or null means unlimited (Enterprise, or not yet known).
 * Same convention as setCachedTier: called wherever SubscriptionGate.jsx's
 * /me resolves; a same-session convenience only, never a grant decision —
 * server/routes/tenants.js enforces the real limit.
 */
export function setCachedLocationLimit(limit) {
  try {
    if (limit === -1 || limit == null) localStorage.removeItem(CACHED_LOCATION_LIMIT_KEY);
    else localStorage.setItem(CACHED_LOCATION_LIMIT_KEY, String(limit));
  } catch { /* localStorage unavailable */ }
}

/** @returns {number|null} the cached limit, or null for "unlimited/unknown" */
export function getCachedLocationLimit() {
  try {
    const raw = localStorage.getItem(CACHED_LOCATION_LIMIT_KEY);
    return raw == null ? null : parseInt(raw, 10);
  } catch { return null; }
}

/**
 * Electron-only: a renewal (or any transaction StoreKit delivers when the
 * app isn't mid-purchase/restore) surfaces as a "pending" transaction —
 * electron/main.cjs pushes 'app-store:pending-transactions'. Each one is
 * server-verified the same way restore() is, so a background renewal (or a
 * quietly-lapsed sub) actually updates the tenant's real tier instead of
 * sitting unclaimed. No-op on Capacitor/web. Returns an unsubscribe function.
 *
 * `claimNow`: also claim whatever is already queued, once, right away. The
 * launch push from main.cjs is sent before the window exists (and before
 * anyone is signed in), so it is lost; the caller passes this when it has an
 * authenticated user, which is also what clears an earlier account's
 * transactions after an account switch. Nothing is delivered to
 * `onEntitlementChange` after the returned unsubscribe ran.
 */
export function watchPendingTransactions(onEntitlementChange, { claimNow = false } = {}) {
  const bridge = isElectron ? window.electronAPI?.appStorePurchase : null;
  if (!bridge?.onPendingTransactions) return () => {};

  let active = true;
  const claimPending = () => serialized(claimPendingOnce);
  const claimPendingOnce = async () => {
    let pending;
    try {
      pending = (await bridge.getPendingTransactions())?.transactions || [];
    } catch {
      return;
    }
    if (pending.length === 0) return;

    // ticket 4556 finding: the Electron pending map (app-store-purchase.cjs)
    // is unbounded and used to be sent in one request — the same overflow
    // bug restore had, except here the server's 400 was swallowed by a bare
    // catch, so every pending transaction silently stayed unverified. Chunk
    // it the same way, and don't let one bad chunk (network blip, oracle
    // outage) abandon every OTHER chunk's transactions too.
    let bestEntitlement = null;
    let hadSuccess = false;
    for (const batch of chunk(pending, RESTORE_BATCH_SIZE)) {
      try {
        const data = await postRestoreBatch(batch);
        hadSuccess = true;
        await finishMarked(batch, data.finishTransactionIds);
        bestEntitlement = betterEntitlement(bestEntitlement, data.entitlement);
      } catch (err) {
        // Surfaced, not swallowed — this batch is left unfinished and
        // retried next launch, but it no longer takes the rest down with it.
        // A refusal (409) still names the verified ones to finish.
        await finishMarked(batch, err.finishTransactionIds);
        console.error('Apple pending-transaction batch failed to verify:', err);
      }
    }

    // Only notify if at least one chunk actually heard back from the server —
    // an all-chunks-failed run (offline, oracle down) leaves everything
    // unclaimed and untouched, same as the old single-request fail-closed behavior.
    if (hadSuccess && active) onEntitlementChange?.(bestEntitlement);
  };

  const unsubscribe = bridge.onPendingTransactions(claimPending);
  if (claimNow) claimPending().catch((err) => console.error('Apple pending-transaction claim failed:', err));
  return () => {
    active = false;
    unsubscribe?.();
  };
}
